import getConfig from 'getConfig';

type VeritasTokenType = 'center' | 'module' | 'symbol' | 'text';

// Internal-only type used during the first tokenization pass.
type InternalTokenType = VeritasTokenType | 'possible-center';

interface VeritasToken {
  type: VeritasTokenType;
  value: string;
}

interface InternalToken {
  type: InternalTokenType;
  value: string;
}

// Keep these module lists in sync with the backend registries in
// benchling/antibodies/format/core/veritas/constants.py
// (VERITAS_CENTER_REGISTRY, VERITAS_MODULE_REGISTRY, EXCLUSIVE_CENTER_REGISTRY).

// Exclusive centers: only in VERITAS_CENTER_REGISTRY, never in VERITAS_MODULE_REGISTRY.
// Always classified as 'center'.
const VERITAS_CENTER_MODULES: ReadonlyArray<string> = [
  'Fc',
  'heteroFc',
  'monoFc',
  'heteroCH3',
  'monoCH3',
  'IgG',
  'heteroIgG',
  'hinge',
  'heteroHinge',
  'monoHinge',
];

// Exclusive centers gated behind config flags. Read at call time rather than module load so the result always
// reflects the current BenchlingConfig.
function getVeritasCenterModules(): ReadonlyArray<string> {
  return getConfig('MOLBIO_ENABLE_SCFC_VERITAS_CENTER')
    ? [...VERITAS_CENTER_MODULES, 'scFc']
    : VERITAS_CENTER_MODULES;
}

// Modules that appear in both VERITAS_CENTER_REGISTRY and VERITAS_MODULE_REGISTRY.
// Classified as 'possible-center' in pass 1, then resolved in pass 2: promoted to
// 'center' (the first one outside brackets, only if no exclusive center exists)
// or demoted to 'module'.
const VERITAS_DUAL_PURPOSE_MODULES: ReadonlyArray<string> = [
  'Fab',
  'scFv',
  'scFab',
  'VHH',
  'CH3',
  'CL',
  'LC',
];

// Appendage-only modules: only in VERITAS_MODULE_REGISTRY, never in VERITAS_CENTER_REGISTRY.
// Always classified as 'module'.
const VERITAS_APPENDAGE_MODULES: ReadonlyArray<string> = [
  'Fd',
  'VH',
  'VL',
  'CH1',
  'CH2',
  'F',
  'dAb', // Alias for VHH; see VERITAS_MODULE_REGISTRY in constants.py
];

const VERITAS_SYMBOLS = new Set(['-', '*', ':', '!', '[', ']', '(', ')']);

const dualPurposeSetLower = new Set(VERITAS_DUAL_PURPOSE_MODULES.map((n) => n.toLowerCase()));

function tokenizeVeritasName(input: string, forceModule?: boolean): Array<VeritasToken> {
  const centerModules = getVeritasCenterModules();
  const centerSetLower = new Set(centerModules.map((n) => n.toLowerCase()));
  const allModuleNames = [...centerModules, ...VERITAS_DUAL_PURPOSE_MODULES, ...VERITAS_APPENDAGE_MODULES];

  // Pass 1: tokenize with dual-purpose modules marked as 'possible-center'.
  // Matching is case-insensitive; token values preserve the original input casing.
  const tokens: Array<InternalToken> = [];
  let i = 0;

  while (i < input.length) {
    const remaining = input.slice(i);
    const nextAlphanumericChunk = remaining.match(/^[a-zA-Z0-9]+/)?.[0];

    const matchedModule = nextAlphanumericChunk
      ? allModuleNames.find((name) => nextAlphanumericChunk.toLowerCase() === name.toLowerCase())
      : null;
    if (matchedModule) {
      const originalValue = input.slice(i, i + matchedModule.length);
      const lowerName = matchedModule.toLowerCase();
      let type: InternalTokenType;
      if (forceModule) {
        type = 'module';
      } else if (centerSetLower.has(lowerName)) {
        type = 'center';
      } else if (dualPurposeSetLower.has(lowerName)) {
        type = 'possible-center';
      } else {
        type = 'module';
      }
      tokens.push({type, value: originalValue});
      i += matchedModule.length;
      continue;
    } else if (nextAlphanumericChunk) {
      tokens.push({type: 'text', value: nextAlphanumericChunk});
      i += nextAlphanumericChunk.length;
      continue;
    }

    if (VERITAS_SYMBOLS.has(input[i])) {
      tokens.push({type: 'symbol', value: input[i]});
      i += 1;
      continue;
    }

    const last = tokens[tokens.length - 1];
    if (last && last.type === 'text') {
      last.value += input[i];
    } else {
      tokens.push({type: 'text', value: input[i]});
    }
    i += 1;
  }

  // Pass 2: resolve 'possible-center' tokens.
  // If an exclusive center exists, all possible-centers become modules.
  // Otherwise, the first possible-center outside brackets becomes the center.
  const hasExclusiveCenter = tokens.some((t) => t.type === 'center');

  if (hasExclusiveCenter) {
    for (const token of tokens) {
      if (token.type === 'possible-center') {
        token.type = 'module';
      }
    }
  } else {
    let promoted = false;
    let inBrackets = false;
    for (const token of tokens) {
      if (token.type === 'symbol' && token.value === '[') {
        inBrackets = true;
      } else if (token.type === 'symbol' && token.value === ']') {
        inBrackets = false;
      } else if (token.type === 'possible-center') {
        if (!promoted && !inBrackets) {
          token.type = 'center';
          promoted = true;
        } else {
          token.type = 'module';
        }
      }
    }
  }

  return tokens as Array<VeritasToken>;
}

export {
  tokenizeVeritasName,
  getVeritasCenterModules,
  VERITAS_DUAL_PURPOSE_MODULES,
  VERITAS_APPENDAGE_MODULES,
  VERITAS_SYMBOLS,
};
export type {VeritasTokenType, VeritasToken};
